"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { EventCard } from "~/components/events/EventCard";
import { EventSearchLauncher } from "~/components/events/EventSearchLauncher";
import { useNearbyCity } from "~/hooks/events/useNearbyCity";
import type { DriverEventDay } from "~/server/api/routers/events";
import { api } from "~/trpc/react";

/**
 * "Events near you" — the strip directly under the hero search card.
 *
 * It lives inside the hero rather than in a section further down on purpose: an
 * event IS a search, just one we can pre-fill entirely, so it belongs beside the
 * thing it's a shortcut for and above the fold where the search card already is.
 * Tapping a tile opens the normal `/search` flow, pre-filled, on results.
 *
 * Rows are event *days*, not events, so a three-day festival contributes three
 * tiles and a driver can pick the one they're going to (see `DriverEventDay`).
 * The limit is applied to days for the same reason, which is why one busy event
 * can legitimately fill the strip.
 *
 * `initialDays` is already on screen when this mounts — `NearbyEventsSection`
 * read the unscoped list on the server and rendered nothing at all if it was
 * empty, so this component never has a loading state and never has an empty one.
 * That is deliberate: the strip sits between the search card and the subtitle,
 * so a skeleton that later collapsed would move the page's actual call to action
 * (and "no events near you" is frequently untrue anyway, since the only
 * geographic bound available is an exact city-string match).
 *
 * All this adds on the client is the city refinement, and only for a driver
 * whose browser will give up a location without being prompted. It swaps the
 * contents of a strip that is already rendered, at the same card count, so it
 * changes no heights.
 */
export function NearbyEvents(t0) {
  const $ = _c(11);
  const {
    initialDays,
    limit
  } = t0;
  const {
    city
  } = useNearbyCity();
  let t1;
  if ($[0] !== city) {
    t1 = city !== null ? {
      city
    } : {};
    $[0] = city;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== limit || $[3] !== t1) {
    t2 = {
      ...t1,
      limit
    };
    $[2] = limit;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = city !== null;
  let t4;
  if ($[5] !== t3) {
    t4 = {
      enabled: t3,
      staleTime: 300000,
      retry: false
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const scopedQuery = api.events.driverList.useQuery(t2, t4);
  const cityDays = scopedQuery.data?.scope === "city" ? scopedQuery.data.days : null;
  const scoped = city !== null && !!cityDays?.length;
  const days = scoped && cityDays ? cityDays : initialDays;
  let t5;
  if ($[7] !== city || $[8] !== days || $[9] !== scoped) {
    t5 = <EventSearchLauncher>{t6 => {
        const {
          openEventSearch,
          busyKey
        } = t6;
        return <StripShell title={scoped ? `Events in ${city}` : "Upcoming events"}>{days.map(day => <EventCard key={day.key} day={day} onOpen={openEventSearch} busy={busyKey === day.key} disabled={busyKey !== null && busyKey !== day.key} />)}</StripShell>;
      }}</EventSearchLauncher>;
    $[7] = city;
    $[8] = days;
    $[9] = scoped;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/**
 * The strip's chrome.
 *
 * A fixed four-up grid, not a scroller: everything on offer has to be visible
 * without a gesture, or the cards past the fourth may as well not exist. Two
 * columns below `md` for the same reason — four across at phone width leaves
 * each card under 90px, too narrow for the name to survive, and a horizontal
 * row would hide three of them.
 *
 * The four-up breakpoint is `md` (768px) rather than `sm`: this theme redefines
 * `--breakpoint-sm` to 375px (`globals.css`), so `sm:` here would fire on every
 * phone wider than an iPhone SE — which is what it used to do.
 *
 * The card count keeps this to one row on desktop and two on a phone, so the
 * hero never grows past that (see `HOME_EVENTS_LIMIT`).
 */
function StripShell(t0) {
  const $ = _c(10);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h2 className="text-[11px] font-bold uppercase tracking-[0.09em] text-secondary">{title}</h2>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Link href="/events" className="inline-flex items-center gap-1 text-[12px] font-bold text-accent-dark transition-colors hover:text-(--blue)">See all<ArrowRight className="h-3 w-3" /></Link>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="mb-2 flex items-center justify-between gap-4 px-0.5">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children) {
    t4 = <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">{children}</div>;
    $[5] = children;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="mb-7 w-full text-left">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}